import React, { FC, useState, useCallback, useMemo } from 'react';

import Table from 'molecules/Table';
import PaginationActions from 'molecules/Table/TablePagination/Actions';
import Link from 'atoms/Link/Link';

import {
  StyledCellContainer,
  StyledTableContainer,
} from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';

import { currencyFormatter } from 'utils/helpers/currencyFormatter';

import { Order } from 'organisms/InvoicesTable/types';

export interface ExpenseRow {
  id: string;
  project_id: number;
  api_id: string;
  amount: number;
  billing_type: string;
  currency: string;
  date: string;
  end_billing_month: string;
  expense: string;
  frequency: string;
  start_billing_month: string;
  user: string;
  row_index: number;
  created_at: string;
  updated_at: string;
  project_name: string;
  company_name: string;
  company_hs_id: number;
}

export type expenseReportSortBy = 'project_name' | 'company_name' | 'expense';

export const initialValue = {
  page: 1,
  limit: 20,
  sort_by: 'project_name',
  sort_order: 'desc',
};

const ExpenseTable: FC<{
  loading?: boolean;
  data: { expenses: ExpenseRow[]; total: number };
  onChange: (f: typeof initialValue) => void;
}> = ({ loading, data: { expenses = [], total = 0 }, onChange }) => {
  const [pagination, setPagination] = useState(initialValue);

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<expenseReportSortBy>('project_name');

  const handleRequestSort = useCallback(
    async (event: React.MouseEvent<unknown>, property: expenseReportSortBy) => {
      const isAsc = orderBy === property && order === 'asc';
      const nextState = {
        ...pagination,
        page: 1,
        sort_by: property,
        sort_order: isAsc ? 'desc' : 'asc',
      };

      onChange(nextState);

      setPagination(nextState);
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [pagination, orderBy, order, onChange]
  );

  const handleChangePage = useCallback(
    async (event: unknown, newPage: number) => {
      const nextState = {
        ...pagination,
        page: newPage + 1,
        sort_by: orderBy,
        sort_order: order,
      };

      onChange(nextState);
      setPagination(nextState);
    },
    [pagination, orderBy, order, onChange]
  );

  const handleChangeRowsPerPage = useCallback(
    async (option: number) => {
      const nextState = {
        limit: option,
        page: 1,
        sort_by: orderBy,
        sort_order: order,
      };

      onChange(nextState);
      setPagination(nextState);
    },
    [orderBy, order, pagination, onChange]
  );

  function setRowKey(row: ExpenseRow, index: number) {
    return { ...row, id: `${row.id}-${index}` };
  }

  const sortedExpenses = useMemo(
    () =>
      [...expenses].sort((left, right) => {
        const comparison = left[orderBy].localeCompare(right[orderBy]);

        return order === 'asc' ? comparison : -comparison;
      }),
    [expenses, order, orderBy]
  );

  const columns = [
    {
      key: 'project_name',
      label: 'Project Name',
      sortable: true,
      width: '20%',
      render: ({ project_name }: ExpenseRow) => (
        <StyledCellText>{project_name}</StyledCellText>
      ),
    },
    {
      key: 'company_name',
      label: 'Company Name',
      sortable: true,
      width: '20%',
      render: ({ company_name, company_hs_id }: ExpenseRow) => (
        <Link to={`/company/${company_hs_id}`} $textDecoration="none">
          <StyledCellText $textType="link">{company_name}</StyledCellText>
        </Link>
      ),
    },
    {
      key: 'expense',
      label: 'Expense Name',
      sortable: true,
      width: '20%',
      render: ({ expense }: ExpenseRow) => (
        <StyledCellText>{expense}</StyledCellText>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: false,
      width: '11%',
      render: ({ amount, currency }: ExpenseRow) => (
        <StyledCellContainer $hStack $right>
          <StyledCellText>
            {amount ? currencyFormatter(amount, currency) : ''}
          </StyledCellText>
        </StyledCellContainer>
      ),
    },
  ];

  return (
    <StyledTableContainer>
      <Table
        data={sortedExpenses.map(setRowKey)}
        columns={columns}
        idKey="id"
        sx={{ minWidth: 650 }}
        aria-label="simple table"
        loading={loading}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
      />
      <StyledTablePagination
        labelDisplayedRows={() => null}
        rowsPerPageOptions={[]}
        count={total}
        rowsPerPage={pagination.limit}
        page={total ? pagination.page - 1 : 0}
        onPageChange={handleChangePage}
        ActionsComponent={(props) => (
          <PaginationActions
            {...props}
            labelRowsPerPage="Items per page"
            rowsPerPageOptions={[20, 50, 100]}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      />
    </StyledTableContainer>
  );
};

export default ExpenseTable;
